import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, ArrowRight, CheckCircle, AlertTriangle, Lightbulb, ThermometerSun, Building2, Wrench, Shield, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const CaseStudyExeterWorkshopRefurb = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Workshop EPC Case Study: Twin Industrial Units Refurbishment Near Exeter",
    "author": { "@type": "Organization", "name": "Energy Performance Direct" },
    "publisher": { "@type": "Organization", "name": "Energy Performance Direct", "url": "https://energyperformancedirect.co.uk" },
    "description": "Real case study of SBEM modelling for twin industrial workshop units near Exeter undergoing refurbishment. Achieving Band B through staged fabric improvements and efficient heating systems."
  };

  return (
    <>
      <Helmet>
        <title>Workshop EPC Case Study | Twin Industrial Units Refurbishment Exeter | Energy Performance Direct</title>
        <meta name="description" content="Real case study: SBEM modelling for twin workshop units near Exeter. How refurbishment with LED lighting, AC heat pumps and insulation improvements achieved Band B from a D baseline." />
        <meta name="keywords" content="workshop EPC case study, industrial unit EPC Exeter, workshop refurbishment EPC, SBEM modelling case study, workshop MEES compliance, industrial energy assessment Devon, commercial EPC refurbishment" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/case-study-exeter-workshop-refurbishment" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <Navbar />
      <Breadcrumbs items={[
        { label: 'Industrial EPC', path: '/industrial-epc' },
        { label: 'Case Study: Workshop Refurbishment', path: '/case-study-exeter-workshop-refurbishment' }
      ]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16" data-testid="case-study-refurb-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm mb-6">
              <Wrench className="w-4 h-4" />
              Real Case Study
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Twin Workshop Units: Refurbishment EPC Modelling Near Exeter
            </h1>
            <p className="text-lg text-gray-300 mb-4">
              How we used SBEM modelling to guide the refurbishment of two adjacent industrial workshop units, achieving Band C with a clear pathway to Band B through targeted insulation and efficient heating systems.
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-gray-400 mt-6">
              <span className="bg-gray-700 px-3 py-1 rounded-full">185.7 sq m per unit</span>
              <span className="bg-gray-700 px-3 py-1 rounded-full">Near Exeter, Devon</span>
              <span className="bg-gray-700 px-3 py-1 rounded-full">Workshop & Offices</span>
              <span className="bg-gray-700 px-3 py-1 rounded-full">Refurbishment Project</span>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p>
                Energy Performance Direct was instructed to carry out <Link to="/sbem-explained" className="text-green-600 hover:underline">SBEM modelling</Link> for two adjacent industrial workshop units near Exeter undergoing refurbishment. Each unit comprises approximately 185.7 square metres of useful floor area, with ground floor workshop space and first floor office accommodation.
              </p>
              <p>
                The client required draft EPCs modelling the intended improvements to confirm the achievable ratings before committing to the refurbishment specification. This is a practical and cost-effective approach: by running the SBEM calculations in advance, the client could confirm that the proposed works would achieve their target rating and identify any additional measures needed to reach a higher band.
              </p>
              <p>
                Although the two units are structurally very similar, their differing orientations mean solar gains affect each building differently, producing slightly different EPC ratings despite near-identical specifications. This is a common occurrence with adjacent industrial units and demonstrates why each building requires its own individual assessment.
              </p>
            </div>

            {/* Key Stats - Twin Comparison */}
            <div className="mt-10">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Rating Comparison</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-2 border-blue-200">
                  <CardContent className="p-6">
                    <h4 className="font-semibold text-lg mb-4 text-blue-700">First Unit</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">D72</p>
                        <p className="text-xs text-gray-600 mt-1">Baseline</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">C72</p>
                        <p className="text-xs text-gray-600 mt-1">After Refurb</p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg col-span-2">
                        <p className="text-2xl font-bold text-blue-600">B41</p>
                        <p className="text-xs text-gray-600 mt-1">With Additional Measures</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-blue-200">
                  <CardContent className="p-6">
                    <h4 className="font-semibold text-lg mb-4 text-blue-700">Second Unit</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">D72</p>
                        <p className="text-xs text-gray-600 mt-1">Baseline</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">C68</p>
                        <p className="text-xs text-gray-600 mt-1">After Refurb</p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg col-span-2">
                        <p className="text-2xl font-bold text-blue-600">B38</p>
                        <p className="text-xs text-gray-600 mt-1">With Additional Measures</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                The 4-point difference between units is caused by orientation and solar impact. This demonstrates why each building requires individual SBEM assessment even when construction is identical.
              </p>
            </div>

            {/* Additional Baseline Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
              <Card className="border-none bg-gray-50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900">185.7</p>
                  <p className="text-sm text-gray-600 mt-1">Floor Area (sq m each)</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-gray-50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900">24.44</p>
                  <p className="text-sm text-gray-600 mt-1">kgCO2/m2/yr BER</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-gray-50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900">261.16</p>
                  <p className="text-sm text-gray-600 mt-1">kWh/m2/yr Primary</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Refurbishment Specification */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Refurbishment Specification Modelled</h2>
            <p className="text-gray-700 mb-8">
              The client provided their intended refurbishment specification, which we entered into the SBEM model to calculate the achievable ratings. The following improvements were modelled as the baseline refurbishment package:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <Lightbulb className="w-8 h-8 text-amber-500 mb-3" />
                  <h3 className="font-semibold text-lg mb-2">LED Lighting Throughout</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /><span>LED light panels at 100 lumens per circuit watt with a Light Output Ratio (LOR) of 1</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /><span>LED bulbs to WC areas with automatic on-off controls</span></li>
                  </ul>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <ThermometerSun className="w-8 h-8 text-red-500 mb-3" />
                  <h3 className="font-semibold text-lg mb-2">Heating and Cooling</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /><span>First floor offices: AC units with minimum COP of 3.5 and SEER of 3.5 with local time and temperature controls</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Ground floor workshop: unheated (no fixed heating)</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Electric heaters with time and temperature controls to WCs and stairwells</span></li>
                  </ul>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <Building2 className="w-8 h-8 text-blue-500 mb-3" />
                  <h3 className="font-semibold text-lg mb-2">Building Modifications</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Front large windows replaced with vehicle access door</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Ground floor workshop and first floor office layout retained</span></li>
                  </ul>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <AlertTriangle className="w-8 h-8 text-amber-500 mb-3" />
                  <h3 className="font-semibold text-lg mb-2">Existing Constraints</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2"><span className="w-4 h-4 bg-amber-100 rounded-full flex-shrink-0 mt-0.5"></span><span>Uninsulated ground floor external wall cavities</span></li>
                    <li className="flex items-start gap-2"><span className="w-4 h-4 bg-amber-100 rounded-full flex-shrink-0 mt-0.5"></span><span>Uninsulated metal frame walls on first floor</span></li>
                    <li className="flex items-start gap-2"><span className="w-4 h-4 bg-amber-100 rounded-full flex-shrink-0 mt-0.5"></span><span>Uninsulated ground floor, exposed floor and roof</span></li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Achieving Band B */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">The Path from C to B</h2>
            <p className="text-gray-300 mb-8">
              With the baseline refurbishment achieving Band C, the client asked what additional measures would bring both units to Band B. Our SBEM modelling identified two straightforward improvements that together deliver the step-change needed.
            </p>

            <div className="space-y-6">
              {/* Quick wins to B */}
              <div className="border-l-4 border-green-500 pl-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-green-600 text-white text-sm font-bold px-3 py-1 rounded-full">Priority Measures</span>
                  <span className="text-green-400 font-semibold">C to B</span>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-white">Insulate above the first floor suspended ceiling</strong>
                      <p className="text-gray-400 text-sm mt-1">Achieve a U-value of 0.42 W/m2K. This requires approximately 100mm of mineral wool insulation above the suspended ceiling, a relatively low-cost measure that delivers significant improvement in the SBEM model.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-white">Add weather compensation controls to the new AC units</strong>
                      <p className="text-gray-400 text-sm mt-1">Weather compensation adjusts heating output based on external temperature, reducing energy consumption. This is often a low-cost addition when specifying new AC systems and delivers meaningful benefit in the SBEM calculation.</p>
                    </div>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">These two measures alone bring the first unit to B41 and the second unit to B38.</p>
              </div>

              {/* Further improvements towards A */}
              <div className="border-l-4 border-blue-500 pl-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-blue-600 text-white text-sm font-bold px-3 py-1 rounded-full">Additional Measures</span>
                  <span className="text-blue-400 font-semibold">Towards Band A</span>
                </div>
                <ul className="space-y-3">
                  {[
                    'Insulate the external walls (cavity fill and/or external insulation to metal frame sections)',
                    'Replace existing windows with double or triple glazing',
                    'Insulate the exposed floor',
                    'Insulate the ground floor',
                    'Add as much insulation as possible to the roof space',
                    'Install solar PV panels on the roof'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-500 mt-4">Each of these measures would further improve the rating. Combining several would push towards Band A.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EPC Recommendations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Baseline EPC Recommendations</h2>
            <p className="text-gray-700 mb-6">
              The baseline EPC assessment identified several long-payback recommendations. While none offered short or medium-term payback on their own, their combined impact is significant when pursuing higher ratings:
            </p>
            <div className="space-y-4">
              {[
                { measure: 'Introduce cavity wall insulation to uninsulated external wall cavities', impact: 'Medium', icon: Layers },
                { measure: 'Install secondary glazing or replace high U-value windows', impact: 'Medium', icon: Building2 },
                { measure: 'Add weather compensation controls to heating system', impact: 'Medium', icon: ThermometerSun },
                { measure: 'Install or improve loft insulation in poorly insulated roof spaces', impact: 'Medium', icon: Shield },
                { measure: 'Consider installing solar water heating', impact: 'Low', icon: Lightbulb }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <item.icon className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-gray-900">{item.measure}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${item.impact === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                    {item.impact} Impact
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Takeaways</h2>
            <div className="space-y-4">
              {[
                'Pre-refurbishment SBEM modelling allows clients to confirm achievable EPC ratings before committing to a specification, avoiding costly mistakes and ensuring the desired band is reached.',
                'Identical buildings can achieve different EPC ratings due to orientation and solar impact. Each unit requires its own individual SBEM assessment even when construction specifications are the same.',
                'Efficient LED lighting with high luminous efficacy (100 lmn/W) and proper controls contributes significantly to achieving good ratings in workshop and office environments.',
                'The step from Band C to Band B can often be achieved through relatively modest additional measures such as ceiling insulation and weather compensation controls, without major structural changes.',
                'AC systems with a COP of 3.5 or better, combined with local time and temperature controls, are highly effective for first floor office spaces above unheated workshop areas.',
                'This advice is impartial and independent of any installers or specific products. We always recommend obtaining 3 to 5 like-for-like quotes to get an accurate idea of installation costs.'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services and Guides</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/workshop-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Workshop EPCs</h3>
                <p className="text-sm text-gray-600">Light industrial and trade premises</p>
              </Link>
              <Link to="/sbem-explained" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">SBEM Explained</h3>
                <p className="text-sm text-gray-600">How commercial EPC ratings are calculated</p>
              </Link>
              <Link to="/mees-commercial-buildings" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">MEES Regulations</h3>
                <p className="text-sm text-gray-600">Minimum energy efficiency standards guide</p>
              </Link>
              <Link to="/industrial-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">All industrial and warehouse assessments</p>
              </Link>
              <Link to="/industrial-epc-devon" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Industrial EPCs Devon</h3>
                <p className="text-sm text-gray-600">Devon industrial EPC assessments</p>
              </Link>
              <Link to="/case-study-exeter-office-workshop" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Case Study: Office & Workshop</h3>
                <p className="text-sm text-gray-600">793.5 sq m G-to-A improvement roadmap</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Planning a Refurbishment? Get Your EPC Modelled First</h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Our accredited assessors can model your intended improvements before work begins, confirming achievable ratings and identifying the most cost-effective route to your target band.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
              <Link to="/booking?service=commercial_warehouse">
                Request Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <a href="tel:01392980077">
                <Phone className="mr-2 w-5 h-5" />
                Call 01392 980077
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default CaseStudyExeterWorkshopRefurb;
